import { bindDialog } from './dialog.mjs';
import { lockPageScroll } from './scroll-lock';

/** The preview and the submitted GET form share the native WooCommerce query. */
const filterUrl = (form, base = form.action) => {
    const url = new URL(base, window.location.href);
    const fields = new FormData(form);
    for (const key of new Set(fields.keys())) url.searchParams.delete(key);
    for (const [key, value] of fields) {
        if (value && !(key === 'ov_price' && value === 'all')) url.searchParams.append(key, value);
    }
    return url;
};

const bindFilterForm = (catalog, form, messages) => {
    const apply = form.querySelector('[data-catalog-filter-apply]');
    const status = form.querySelector('[data-catalog-filter-status]');
    const groups = [...form.querySelectorAll('[data-catalog-filter-group]')];
    const categories = [...form.querySelectorAll('[data-catalog-category]')];
    const openGroups = groups.map((group) => group.open);
    const counts = new Map([[filterUrl(form).href, Number(catalog.dataset.total)]]);
    let request;
    let revision = 0;
    let delay;

    const cancel = () => {
        revision += 1;
        window.clearTimeout(delay);
        request?.abort();
        apply.removeAttribute('aria-busy');
    };
    const showCount = (count) => {
        apply.textContent = messages.filterApply.replace('{count}', count.toLocaleString('pl-PL'));
        status.classList.toggle('CatalogFilters__status--quiet', count !== 0);
        status.textContent = count === 0 ? messages.filterEmpty : messages.filterResults.replace('{count}', count.toLocaleString('pl-PL'));
    };
    const updateSelection = () => {
        const custom = form.querySelector('[name="ov_price"]:checked')?.value === 'custom';
        form.querySelectorAll('[name="min_price"], [name="max_price"]').forEach((input) => { input.disabled = !custom; });
        groups.forEach((group) => {
            const selected = [...group.querySelectorAll('input:checked')].map((input) => input.nextElementSibling.textContent.trim());
            const summary = group.querySelector('[data-catalog-filter-selection]');
            summary.textContent = selected.join(', ');
            summary.hidden = selected.length === 0;
        });
        // Category navigation retains draft filters, ordering and the search term.
        categories.forEach((link) => { link.href = filterUrl(form, link.dataset.catalogCategory).href; });
    };
    const preview = async (version, url) => {
        const controller = new AbortController();
        request = controller;
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        try {
            if (url.origin !== window.location.origin) throw new Error('Unexpected catalogue origin');
            const response = await fetch(url.href, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const next = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-catalog]');
            const count = Number(next?.dataset.total);
            if (!next || !Number.isSafeInteger(count) || count < 0) throw new Error('Invalid catalogue count');
            if (version !== revision) return;
            if (counts.size >= 20) counts.delete(counts.keys().next().value);
            counts.set(url.href, count);
            showCount(count);
        } catch (error) {
            if (version === revision) {
                apply.textContent = messages.filterSubmit;
                status.classList.remove('CatalogFilters__status--quiet');
                status.textContent = messages.filterError;
            }
        } finally {
            window.clearTimeout(timeout);
            if (version === revision) apply.removeAttribute('aria-busy');
        }
    };
    const change = () => {
        cancel();
        updateSelection();
        const url = filterUrl(form);
        if (counts.has(url.href)) {
            showCount(counts.get(url.href));
            return;
        }
        apply.textContent = messages.filterSubmit;
        apply.setAttribute('aria-busy', 'true');
        status.classList.add('CatalogFilters__status--quiet');
        status.textContent = messages.filterChecking;
        const version = revision;
        delay = window.setTimeout(() => preview(version, url), 300);
    };

    form.addEventListener('change', change);
    form.addEventListener('submit', () => {
        cancel();
        updateSelection();
    });
    form.querySelector('[data-catalog-filter-reset]').addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        form.querySelectorAll('input[type="checkbox"]').forEach((input) => { input.checked = false; });
        const allPrices = form.querySelector('[name="ov_price"][value="all"]');
        if (allPrices) allPrices.checked = true;
        change();
    });
    updateSelection();

    return {
        discard() {
            cancel();
            form.reset();
            groups.forEach((group, index) => { group.open = openGroups[index]; });
            updateSelection();
            showCount(Number(catalog.dataset.total));
        },
    };
};

export const initCatalogFilters = (catalog, messages) => {
    const fallback = catalog.querySelector('[data-catalog-filter-fallback]');
    const trigger = catalog.querySelector('[data-catalog-filter-trigger]');
    const content = fallback?.querySelector('[data-catalog-filter-content]');
    if (!trigger || !content) return;

    const form = content.querySelector('form');
    const filters = messages ? bindFilterForm(catalog, form, messages) : null;
    if (typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) {
        trigger.addEventListener('click', () => { fallback.open = true; });
        return;
    }
    const dialog = document.createElement('dialog');
    dialog.id = 'catalog-filters';
    dialog.className = 'CatalogFilterDialog';
    dialog.setAttribute('aria-labelledby', 'catalog-filter-heading');
    dialog.append(content);
    document.body.append(dialog);
    fallback.hidden = true;
    content.querySelector('[data-dialog-close]').hidden = false;
    const heading = content.querySelector('#catalog-filter-heading');
    heading.setAttribute('autofocus', '');
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', dialog.id);
    let unlock;
    trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (dialog.open) return;
        unlock = lockPageScroll();
        content.querySelector('.CatalogFilters__fields').scrollTop = 0;
    });
    const controller = bindDialog(dialog, [trigger], { closeAnimation: 'catalog-panel-exit' });
    dialog.addEventListener('close', () => {
        unlock?.();
        unlock = null;
        filters?.discard();
    });
    window.addEventListener('pageshow', (event) => {
        if (event.persisted && dialog.open) controller.close();
    });
    // Dragging a scrollbar or releasing a gesture outside the panel must not close it.
    let backdropStart = false;
    dialog.addEventListener('pointerdown', (event) => { backdropStart = event.target === dialog; });
    dialog.addEventListener('pointercancel', () => { backdropStart = false; });
    dialog.addEventListener('click', (event) => {
        if (backdropStart && event.target === dialog) controller.close();
        backdropStart = false;
    });
};
